// ── Public judge library API client (EX-PUB01) ──────────────────────────────
// The public routes are softly authenticated: `fetcher` attaches the bearer
// when `AuthProvider` has one, and the server annotates solved state only then.
// Nothing here ever sends a `contextType` — the practice band is the route's,
// not the client's (`docs/27` §8.4.1 decision 5).

import { fetcher, type ApiEnvelope } from "@/lib/api";
import { laneRoute, runThroughLane } from "./lib/run-lane";
import type { CodeFixResult } from "@/features/playground/types";
import type {
  JudgeProblemListParams,
  JudgeProblemListResponse,
  JudgeProblemEditorial,
  JudgeProblemStats,
  JudgeProblemSubmissionsParams,
  JudgeProblemSubmissionsResponse,
  JudgeProblemView,
  JudgeSubmissionSource,
  JudgeRuntimeCatalogue,
  JudgeTopics,
  PracticeHints,
  PracticeSubmissionDetail,
  PracticeSubmitInput,
  PracticeSubmitResult,
  RevealPracticeHintInput,
  RevealPracticeHintResult,
  RunSampleInput,
  SampleRunResult,
  SubmissionHistoryParams,
  SubmissionHistoryResponse,
} from "./types";

function unwrap<T>(res: ApiEnvelope<T>): T {
  if (!res.success) throw new Error(res.error.message);
  return res.data;
}

function buildQuery(params: Record<string, string | number | undefined>): string {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== "") query.set(key, String(value));
  }
  const encoded = query.toString();
  return encoded ? `?${encoded}` : "";
}

export async function fetchJudgeProblems(
  params: JudgeProblemListParams = {},
): Promise<JudgeProblemListResponse> {
  const res = await fetcher<JudgeProblemListResponse>(
    `/v1/judge/problems${buildQuery({
      page: params.page,
      limit: params.limit,
      search: params.search,
      difficulty: params.difficulty,
      tag: params.tag,
      solved: params.solved,
      sort: params.sort,
      kind: params.kind,
      ratingMin: params.ratingMin,
      ratingMax: params.ratingMax,
    })}`,
  );
  return unwrap(res);
}

export async function fetchJudgeProblem(slug: string): Promise<JudgeProblemView> {
  const res = await fetcher<{ problem: JudgeProblemView }>(
    `/v1/judge/problems/${encodeURIComponent(slug)}`,
  );
  return unwrap(res).problem;
}

/** `EX-EDT01`: the editorial, asked for only when the reader opens it. */
export async function fetchJudgeProblemEditorial(slug: string): Promise<JudgeProblemEditorial> {
  const res = await fetcher<JudgeProblemEditorial>(
    `/v1/judge/problems/${encodeURIComponent(slug)}/editorial`,
  );
  return unwrap(res);
}

/** `EX-STA01`: the statistics tab, asked for when the tab opens. */
export async function fetchJudgeProblemStats(slug: string): Promise<JudgeProblemStats> {
  const res = await fetcher<JudgeProblemStats>(`/v1/judge/problems/${encodeURIComponent(slug)}/stats`);
  return unwrap(res);
}

/** `EX-SRC01`: the Submissions tab's list, filtered. */
export async function fetchJudgeProblemSubmissions(
  slug: string,
  params: JudgeProblemSubmissionsParams,
): Promise<JudgeProblemSubmissionsResponse> {
  const search = new URLSearchParams({ page: String(params.page), limit: String(params.limit) });
  if (params.verdict) search.set("verdict", params.verdict);
  if (params.language) search.set("language", params.language);
  if (params.mine) search.set("mine", "1");
  const res = await fetcher<JudgeProblemSubmissionsResponse>(
    `/v1/judge/problems/${encodeURIComponent(slug)}/submissions?${search.toString()}`,
  );
  return unwrap(res);
}

/** `EX-SRC01`: one program; the server's gate decides (ADR-0082). */
export async function fetchJudgeSubmissionSource(slug: string, submissionId: string): Promise<JudgeSubmissionSource> {
  const res = await fetcher<JudgeSubmissionSource>(
    `/v1/judge/problems/${encodeURIComponent(slug)}/submissions/${encodeURIComponent(submissionId)}/source`,
  );
  return unwrap(res);
}

/** `EX-TAG01`: the library's topics with their counts, most problems first. */
export async function fetchJudgeTopics(): Promise<JudgeTopics> {
  const res = await fetcher<Partial<JudgeTopics>>(`/v1/judge/problem-tags`);
  const data = unwrap(res);
  return {
    items: Array.isArray(data.items) ? data.items : [],
    total: data.total ?? 0,
    libraryIndexable: data.libraryIndexable === true,
  };
}

export async function fetchJudgeRuntimes(contestSlug?: string): Promise<JudgeRuntimeCatalogue> {
  const res = await fetcher<JudgeRuntimeCatalogue>(contestSlug ? `/v1/contests/${encodeURIComponent(contestSlug)}/runtimes` : `/v1/judge/runtimes`);
  return unwrap(res);
}

/** Enqueues a graded practice submission in the practice band (202). */
export async function submitPractice(input: PracticeSubmitInput): Promise<PracticeSubmitResult> {
  const res = await fetcher<PracticeSubmitResult>(`/v1/judge/submissions`, {
    method: "POST",
    body: JSON.stringify(input),
  });
  return unwrap(res);
}

/**
 * Feedback only: no submission row, no submit slot (`EX-PUB12`). Waits its
 * turn for a share of the judge (`EX-SRN01`) rather than being refused.
 */
export async function runPracticeSample(
  slug: string,
  input: RunSampleInput,
): Promise<SampleRunResult> {
  return runThroughLane(async () =>
    unwrap(
      await fetcher<SampleRunResult>(laneRoute(`/v1/judge/problems/${encodeURIComponent(slug)}/run`), {
        method: "POST",
        body: JSON.stringify(input),
      }),
    ),
  );
}

export async function fetchPracticeSubmission(id: string): Promise<PracticeSubmissionDetail> {
  const res = await fetcher<PracticeSubmissionDetail>(
    `/v1/judge/submissions/${encodeURIComponent(id)}`,
  );
  return unwrap(res);
}

export async function fetchMySubmissions(
  params: SubmissionHistoryParams = {},
): Promise<SubmissionHistoryResponse> {
  const res = await fetcher<SubmissionHistoryResponse>(
    `/v1/judge/submissions${buildQuery({
      page: params.page,
      limit: params.limit,
      problemId: params.problemId,
    })}`,
  );
  return unwrap(res);
}

/** `EX-HNT01`: the reader's three practice steps on this problem. */
export async function fetchPracticeHints(slug: string, locale: "en" | "bn"): Promise<PracticeHints> {
  const res = await fetcher<PracticeHints>(
    `/v1/judge/problems/${encodeURIComponent(slug)}/hints${buildQuery({ locale })}`,
  );
  return unwrap(res);
}

/** `EX-HNT01`: show one step; an AI step spends one credit, a replay of its key none. */
export async function revealPracticeHint(
  slug: string,
  input: RevealPracticeHintInput,
): Promise<RevealPracticeHintResult> {
  const { step, ...body } = input;
  const res = await fetcher<RevealPracticeHintResult>(
    `/v1/judge/problems/${encodeURIComponent(slug)}/hints/${step}`,
    { method: "POST", body: JSON.stringify(body) },
  );
  return unwrap(res);
}

/**
 * `EX-HNT01`: Fix My Error on the reader's own compile-error submission. The
 * server reads the program and the compiler's words; the answer is the
 * Playground's, plus the program it was worked out against. The key travels
 * twice, header and body, as the Playground's does.
 */
export async function fixPracticeError(
  slug: string,
  submissionId: string,
  input: { locale: "en" | "bn"; idempotencyKey: string },
): Promise<CodeFixResult & { sourceCode: string }> {
  const res = await fetcher<CodeFixResult & { sourceCode: string }>(
    `/v1/judge/problems/${encodeURIComponent(slug)}/submissions/${encodeURIComponent(submissionId)}/fix-error`,
    {
      method: "POST",
      headers: { "Idempotency-Key": input.idempotencyKey },
      body: JSON.stringify(input),
    },
  );
  return unwrap(res);
}
